import React, { FC, useState, useEffect, useMemo } from "react";
import { Box, Button, FieldErrorText, Heading, Select, Text, Container as TeddyContainer } from "@telia/teddy";
import NexusLogoSymbol from "../../components/atoms/NexusLogoSymbol/NexusLogoSymbol";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../../models";
import { Dealer } from "../../../../../models/state";
import { useSelectedRetailer } from "../../hooks/useSelectedRetailer";
import { addRetailerInfo, idpAuthorize } from "../../state/actions";
import { getSalesAgent, getShops } from "../../state/selectors/selectors";
import SupportLinks from "../../components/molecules/SupportLinks/SupportLinks";
import "./Shops.less";

const ShopSelectionPage: FC = () => {
    const dispatch = useDispatch();
    const [selectedShop, setSelectedShop] = useState<Dealer | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const shops = useSelector((state: RootState) => getShops(state));
    const salesAgent = useSelector((state: RootState) => getSalesAgent(state));
    const { selectedRetailer, saveSelectedRetailer } = useSelectedRetailer();

    useEffect(() => {
        dispatch(idpAuthorize());
    }, []);

    const sortedShops = useMemo(
        () => [...shops].sort((a, b) => `${a.dealerName}`.localeCompare(`${b.dealerName}`, "no-NB")),
        [shops],
    );

    useEffect(() => {
        let shopToSelect: Dealer | undefined;

        // First try to find by selectedRetailer.dealerId
        if (selectedRetailer) {
            shopToSelect = sortedShops.find((s) => s.dealerId === selectedRetailer.dealerId);
        }

        // If not found, try to find by loggedInAgent.dealerCode
        if (!shopToSelect && salesAgent?.dealerCode) {
            shopToSelect = sortedShops.find((s) => s.dealerCode === salesAgent.dealerCode);
        }

        if (shopToSelect) {
            setSelectedShop(shopToSelect);
        }
    }, [selectedRetailer, sortedShops, salesAgent]);

    const handleShopSelect = (dealerId?: number) => {
        const shop = sortedShops.find((s) => s.dealerId === dealerId);
        if (shop) {
            setSelectedShop(shop);
        }
    };

    const handleContinue = async (): Promise<void> => {
        if (!selectedShop || !salesAgent) return;

        setIsSubmitting(true);
        setError(null);

        try {
            await dispatch(
                addRetailerInfo(
                    selectedShop.dealerId,
                    selectedShop.dealerCode,
                    selectedShop.dealerName,
                    selectedShop.chainName,
                    selectedShop.isManager,
                ),
            );

            saveSelectedRetailer({
                chainName: selectedShop.chainName,
                dealerId: selectedShop.dealerId,
                dealerCode: selectedShop.dealerCode,
                dealerName: selectedShop.dealerName,
                isManager: selectedShop.isManager,
            });

            window.location.assign("/nexus/nyordre/");
        } catch (err: unknown) {
            if (err && typeof err === "object" && "displayableMessage" in err) {
                setError(err.displayableMessage as string);
            } else {
                setError(err instanceof Error ? err.message : "Det oppsto en ukjent feil [1]");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="shop-selection-page"
            style={{ backgroundImage: "url(/shop/assets/img-static/retailer/reseller-background.png)" }}
        >
            <div className="shop-selection-page__background">
                <TeddyContainer className="shop-selection-page__container">
                    <Box className="shop-selection-page__card">
                        <div className="shop-selection-page__logo">
                            <NexusLogoSymbol />
                            <Heading mb="100" as="h1" variant="title-300" className="shop-selection-page__logo-text">
                                Telia Nexus
                            </Heading>
                        </div>
                        <Heading mb="100" as="h2" variant="title-100" className="shop-selection-page__heading">
                            Velg butikk for å fullføre innlogging
                        </Heading>
                        <div className="shop-selection-page__form">
                            {shops.length === 0 ? (
                                <Text>
                                    {(() => {
                                        const displayName = [salesAgent?.firstName, salesAgent?.lastName]
                                            .filter(Boolean)
                                            .join(" ");

                                        if (!salesAgent?.id) {
                                            return "Brukeren din er ikke knyttet til en butikk, ta kontakt med din nærmeste leder.";
                                        }

                                        const userDetails = displayName
                                            ? ` (${displayName}, ${salesAgent.id})`
                                            : ` (${salesAgent.id})`;

                                        return `Din bruker${userDetails} er ikke knyttet til en butikk, ta kontakt med din nærmeste leder.`;
                                    })()}
                                </Text>
                            ) : (
                                <>
                                    <Select
                                        label="Velg butikk"
                                        name="shop"
                                        options={sortedShops.map((shop) => `${shop.dealerId}`)}
                                        value={`${selectedShop?.dealerId || ""}`}
                                        className="shop-selection-page__select"
                                        onValueChange={(value: string) => {
                                            handleShopSelect(parseInt(value, 10));
                                            return value;
                                        }}
                                    >
                                        <Select.Trigger className="shop-selection-page__select-trigger">
                                            <Select.Value placeholder="Velge..." />
                                        </Select.Trigger>
                                        <Select.Portal>
                                            <Select.Content className="shop-selection-page__select-content">
                                                <Select.Viewport>
                                                    {sortedShops.map((shop) => (
                                                        <Select.Item key={shop.dealerId} value={`${shop.dealerId}`}>
                                                            {shop.dealerName}
                                                            {shop.dealerCode ? ` (${shop.dealerCode})` : ""}
                                                        </Select.Item>
                                                    ))}
                                                </Select.Viewport>
                                            </Select.Content>
                                        </Select.Portal>
                                    </Select>

                                    {error && (
                                        <FieldErrorText className="shop-selection-page__error-text">
                                            {error}
                                        </FieldErrorText>
                                    )}
                                    <Button
                                        onClick={handleContinue}
                                        disabled={!selectedShop}
                                        className="shop-selection-page__continue-button"
                                        loading={isSubmitting}
                                    >
                                        Fortsett
                                    </Button>
                                </>
                            )}
                        </div>
                        <SupportLinks />
                    </Box>
                </TeddyContainer>
            </div>
        </div>
    );
};

export default ShopSelectionPage;
